import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { PlusIcon, TrashIcon, CalendarIcon, MailIcon, SmartphoneIcon, CalendarDaysIcon, UsersIcon, BuildingIcon, PercentIcon, DollarSignIcon, AlertCircleIcon, InfoIcon, ChevronDownIcon, ChevronUpIcon, UserIcon } from 'lucide-react';
import { MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
import { SearchableSelect } from '../../../shared/ui/SearchableSelect';
import { mockSuppliers } from '../../suppliers/mocks/mockSuppliers';
export interface Recipient {
  id: number;
  email: string;
  phone: string;
}
export interface Supplier {
  id: number;
  name: string;
  email: string;
  amount: string;
  percentage?: string;
}
export interface Installment {
  id: number;
  amount: string;
  dueDate: string;
  sendLinkDate: string;
  description: string;
  recipient?: Recipient;
}
interface InstallmentPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onSplitTypeChange?: (type: 'dates' | 'recipients' | 'commission') => void;
  onInstallmentsChange?: (installments: Installment[]) => void;
  onSuppliersChange?: (suppliers: Supplier[]) => void;
  onBeneficiaryTypeChange?: (type: 'percentage' | 'fixed') => void;
  onTotalPaymentAmountChange?: (amount: string) => void;
}
export function InstallmentPayment({
  onExpiryDateChange,
  onSplitTypeChange,
  onInstallmentsChange,
  onSuppliersChange,
  onBeneficiaryTypeChange,
  onTotalPaymentAmountChange
}: InstallmentPaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [installments, setInstallments] = useState<Installment[]>([{
    id: 1,
    amount: '',
    dueDate: '',
    sendLinkDate: '',
    description: '',
    recipient: {
      id: 1,
      email: '',
      phone: ''
    }
  }, {
    id: 2,
    amount: '',
    dueDate: '',
    sendLinkDate: '',
    description: '',
    recipient: {
      id: 2,
      email: '',
      phone: ''
    }
  }]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([{
    id: 1,
    name: '',
    email: '',
    amount: '',
    percentage: '0'
  }]);
  const [splitType, setSplitType] = useState<'dates' | 'recipients' | 'commission'>('dates');
  const [beneficiaryType, setBeneficiaryType] = useState<'percentage' | 'fixed'>('percentage');
  const [feeType, setFeeType] = useState<'supplier' | 'merchant' | 'split' | 'customer'>('supplier');
  const [passFeesToCustomer, setPassFeesToCustomer] = useState(false);
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [totalPaymentAmount, setTotalPaymentAmount] = useState('');
  const [commissionError, setCommissionError] = useState<string | null>(null);
  const [supplierCommissionTotal, setSupplierCommissionTotal] = useState(0);
  const [showDatePickers, setShowDatePickers] = useState<{
    [key: number]: {
      due: boolean;
      send: boolean;
    };
  }>({});
  // This would come from settings in a real app
  const defaultExpiryDays = 30;
  // Validate commission amounts don't exceed total payment
  useEffect(() => {
    if (splitType === 'commission' && totalPaymentAmount) {
      const totalAmount = parseFloat(totalPaymentAmount) || 0;
      // Calculate total commission amount
      const commissionTotal = suppliers.reduce((total, supplier) => {
        if (beneficiaryType === 'percentage') {
          // For percentage, we'll check in another validation
          return total;
        } else {
          // For fixed amount
          return total + (parseFloat(supplier.amount) || 0);
        }
      }, 0);
      setSupplierCommissionTotal(commissionTotal);
      if (beneficiaryType === 'fixed' && commissionTotal > totalAmount) {
        setCommissionError('Total commission amount cannot exceed the total payment amount');
      } else if (beneficiaryType === 'percentage') {
        const percentageTotal = suppliers.reduce((total, supplier) => {
          return total + (parseFloat(supplier.percentage || '0') || 0);
        }, 0);
        if (percentageTotal > 100) {
          setCommissionError('Total commission percentage cannot exceed 100%');
        } else {
          setCommissionError(null);
        }
      } else {
        setCommissionError(null);
      }
    }
  }, [splitType, totalPaymentAmount, suppliers, beneficiaryType]);
  // Report state changes to parent
  useEffect(() => {
    onSplitTypeChange?.(splitType);
  }, [splitType]);
  useEffect(() => {
    onInstallmentsChange?.(installments);
  }, [installments]);
  useEffect(() => {
    onSuppliersChange?.(suppliers);
  }, [suppliers]);
  useEffect(() => {
    onBeneficiaryTypeChange?.(beneficiaryType);
  }, [beneficiaryType]);
  useEffect(() => {
    onTotalPaymentAmountChange?.(totalPaymentAmount);
  }, [totalPaymentAmount]);
  // Calculate send link date when due date changes
  const calculateSendLinkDate = (dueDateStr: string): string => {
    if (!dueDateStr) return '';
    try {
      // Parse the due date
      const [month, day, year] = dueDateStr.split('/').map(part => parseInt(part));
      const dueDate = new Date(year, month - 1, day);
      // Subtract the default expiry days
      const sendDate = new Date(dueDate);
      sendDate.setDate(sendDate.getDate() - defaultExpiryDays);
      // Format the date as MM/DD/YYYY
      const sendMonth = (sendDate.getMonth() + 1).toString().padStart(2, '0');
      const sendDay = sendDate.getDate().toString().padStart(2, '0');
      const sendYear = sendDate.getFullYear();
      return `${sendMonth}/${sendDay}/${sendYear}`;
    } catch (error) {
      // If there's any error parsing, return empty string
      return '';
    }
  };
  const addInstallment = () => {
    const newId = Math.max(...installments.map(i => i.id), 0) + 1;
    setInstallments([...installments, {
      id: newId,
      amount: '',
      dueDate: '',
      sendLinkDate: '',
      description: '',
      recipient: {
        id: newId,
        email: '',
        phone: ''
      }
    }]);
  };
  const addSupplier = () => {
    const newId = Math.max(...suppliers.map(s => s.id), 0) + 1;
    setSuppliers([...suppliers, {
      id: newId,
      name: '',
      email: '',
      amount: '',
      percentage: '0'
    }]);
  };
  const removeInstallment = (id: number) => {
    if (installments.length > 1) {
      setInstallments(installments.filter(i => i.id !== id));
    }
  };
  const removeSupplier = (id: number) => {
    if (suppliers.length > 1) {
      setSuppliers(suppliers.filter(s => s.id !== id));
    }
  };
  const updateInstallment = (id: number, field: string, value: string) => {
    setInstallments(installments.map(installment => {
      if (installment.id === id) {
        const updatedInstallment = {
          ...installment,
          [field]: value
        };
        // If we're updating the due date and it's a valid date, update the send link date
        if (field === 'dueDate' && value) {
          updatedInstallment.sendLinkDate = calculateSendLinkDate(value);
        }
        return updatedInstallment;
      }
      return installment;
    }));
  };
  const updateSupplier = (id: number, field: string, value: string) => {
    setSuppliers(suppliers.map(supplier => supplier.id === id ? {
      ...supplier,
      [field]: value
    } : supplier));
  };
  const updateRecipient = (installmentId: number, field: 'email' | 'phone', value: string) => {
    setInstallments(installments.map(installment => installment.id === installmentId && installment.recipient ? {
      ...installment,
      recipient: {
        ...installment.recipient,
        [field]: value
      }
    } : installment));
  };
  const splitOptions = [{
    value: 'dates',
    label: 'By payment schedule',
    icon: <CalendarDaysIcon size={16} data-id="element-7322" />
  }, {
    value: 'recipients',
    label: 'By multiple people',
    icon: <UsersIcon size={16} data-id="element-7323" />
  }, {
    value: 'commission',
    label: 'By commission',
    icon: <PercentIcon size={16} data-id="element-7324" />
  }];
  const beneficiaryOptions = [{
    value: 'percentage',
    label: 'Percentage',
    icon: <PercentIcon size={16} data-id="element-7325" />
  }, {
    value: 'fixed',
    label: 'Fixed amount',
    icon: <DollarSignIcon size={16} data-id="element-7326" />
  }];
  const totalAmount = installments.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const supplierOptions = mockSuppliers.map(s => s.name);
  // Mock suppliers list for dropdown
  const suppliersList = [{
    id: 1,
    name: 'ABC Suppliers',
    email: 'abc@example.com'
  }, {
    id: 2,
    name: 'XYZ Merchants',
    email: 'xyz@example.com'
  }, {
    id: 3,
    name: '123 Distributors',
    email: '123@example.com'
  }, {
    id: 4,
    name: 'Global Partners',
    email: 'global@example.com'
  }];
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    if (onExpiryDateChange) {
      onExpiryDateChange(date);
    }
  };
  const handleTotalPaymentAmountChange = (value: string) => {
    setTotalPaymentAmount(value);
  };
  const getCommissionAmountDisplay = () => {
    if (beneficiaryType === 'fixed') {
      return `${formatCurrencyAmount(supplierCommissionTotal, {
        fractionDigits: 2
      })} of ${formatCurrencyAmount(parseFloat(totalPaymentAmount || '0'), {
        fractionDigits: 2
      })}`;
    } else {
      const percentageTotal = suppliers.reduce((total, supplier) => {
        return total + (parseFloat(supplier.percentage || '0') || 0);
      }, 0);
      return `${percentageTotal.toFixed(2)}% of payment`;
    }
  };
  // Date picker helpers
  const toggleDatePicker = (id: number, type: 'due' | 'send') => {
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: prev[id] ? !prev[id][type] : true
      }
    }));
  };
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  const handleDateSelect = (id: number, field: 'dueDate' | 'sendLinkDate', date: Date) => {
    // Update in a single state call to avoid stale state overwrites
    setInstallments(installments.map(installment => {
      if (installment.id === id) {
        const updated = {
          ...installment,
          [field]: formatDate(date)
        };
        // If selecting due date, also auto-calculate send link date
        if (field === 'dueDate') {
          const sendDate = new Date(date);
          sendDate.setDate(sendDate.getDate() - defaultExpiryDays);
          updated.sendLinkDate = formatDate(sendDate);
        }
        return updated;
      }
      return installment;
    }));
    // Close the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field === 'dueDate' ? 'due' : 'send']: false
      }
    }));
  };
  const generateCalendar = (dateStr: string) => {
    const today = new Date();
    const selectedDate = dateStr ? parseDate(dateStr) : today;
    const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysInMonth = lastDay.getDate();
    const weeks = [];
    let days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), i);
      days.push(date);
      if (days.length === 7 || i === daysInMonth) {
        // Fill remaining days of the week
        while (days.length < 7) {
          days.push(null);
        }
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: selectedDate,
      weeks
    };
  };
  const parseDate = (dateString: string): Date => {
    if (!dateString) return new Date();
    const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10));
    return new Date(year, month - 1, day);
  };
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date, selectedDateStr: string): boolean => {
    if (!selectedDateStr) return false;
    const selectedDate = parseDate(selectedDateStr);
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const handlePrevMonth = (calendar: any, id: number, type: 'due' | 'send') => {
    const date = new Date(calendar.month);
    date.setMonth(date.getMonth() - 1);
    // Force re-render by toggling the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: false
      }
    }));
    setTimeout(() => {
      setShowDatePickers(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [type]: true
        }
      }));
    }, 10);
  };
  const handleNextMonth = (calendar: any, id: number, type: 'due' | 'send') => {
    const date = new Date(calendar.month);
    date.setMonth(date.getMonth() + 1);
    // Force re-render by toggling the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: false
      }
    }));
    setTimeout(() => {
      setShowDatePickers(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [type]: true
        }
      }));
    }, 10);
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  const labelClass = 'block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2';
  return <div className="space-y-5" data-id="element-7327">
      {/* ─── SPLIT PAYMENT CONFIGURATION ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7328">
        <div className="p-6 space-y-6" data-id="element-7329">
          <div data-id="element-7330">
            <label className={labelClass} data-id="element-7331">
              {t('components.InstallmentPayment.how_would_you_like_to_split')}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-7332">
              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${splitType === 'dates' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-7333">
                <input type="radio" name="splitType" className="mt-0.5 h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={splitType === 'dates'} onChange={() => setSplitType('dates')} data-id="element-7334" />

                <div data-id="element-7335">
                  <div className="flex items-center gap-2 mb-1" data-id="element-7336">
                    <CalendarDaysIcon size={16} className={splitType === 'dates' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-7337" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-7338">
                      {t('components.InstallmentPayment.payment_schedule')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-7339">
                    {t('components.InstallmentPayment.split_into_multiple_payments_on_different')}
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${splitType === 'recipients' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-7340">
                <input type="radio" name="splitType" className="mt-0.5 h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={splitType === 'recipients'} onChange={() => setSplitType('recipients')} data-id="element-7341" />

                <div data-id="element-7342">
                  <div className="flex items-center gap-2 mb-1" data-id="element-7343">
                    <UsersIcon size={16} className={splitType === 'recipients' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-7344" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-7345">
                      {t('components.InstallmentPayment.multiple_people')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-7346">
                    {t('components.InstallmentPayment.split_the_payment_between_different_recipients')}
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${splitType === 'commission' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-7347">
                <input type="radio" name="splitType" className="mt-0.5 h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={splitType === 'commission'} onChange={() => setSplitType('commission')} data-id="element-7348" />

                <div data-id="element-7349">
                  <div className="flex items-center gap-2 mb-1" data-id="element-7350">
                    <PercentIcon size={16} className={splitType === 'commission' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-7351" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-7352">
                      {t('components.InstallmentPayment.commission_split')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-7353">
                    {t('components.InstallmentPayment.distribute_payment_between_your_business_and')}
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div data-id="element-7354">
            <label className={labelClass} data-id="element-7355">
              {t('components.InstallmentPayment.order_reference')}
            </label>
            <input type="text" className={inputClass} placeholder={t('components.InstallmentPayment.attrs.eg_order_1234_or_project_name')} data-id="element-7356" />
          </div>
        </div>
      </div>

      {/* ─── SPLIT DETAILS ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-7357">
        <div className="px-6 py-4 flex items-start justify-between border-b border-gray-100" data-id="element-7358">
          <div data-id="element-7359">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-7360">
              {splitType === 'dates' ? 'Payment schedule' : splitType === 'recipients' ? 'Payment recipients' : 'Commission details'}
            </h3>
            {splitType === 'dates' && <p className="text-xs text-gray-500 mt-0.5 max-w-2xl" data-id="element-7361">
                {t('components.InstallmentPayment.payment_links_will_be_automatically_sent')}{' '}
                {defaultExpiryDays}{' '}
                {t('components.InstallmentPayment.days_before_the_due_date')}
              </p>}
            {splitType === 'commission' && <p className="text-xs text-gray-500 mt-0.5" data-id="element-7362">
                {t('components.InstallmentPayment.configure_supplier_commissions_and_how_payments')}
              </p>}
          </div>
          <div className="text-sm font-medium text-gray-500 flex-shrink-0" data-id="element-7363">
            {splitType === 'commission' ? `Commission: ${getCommissionAmountDisplay()}` : `Total: ${formatCurrencyAmount(totalAmount, {
            fractionDigits: 2
          })}`}
          </div>
        </div>
        <div className="p-6" data-id="element-7364">
          {/* Commission section */}
          {splitType === 'commission' && <div className="space-y-6" data-id="element-7365">
              {/* Commission configuration - moved from top card */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-7366">
                <div data-id="element-7367">
                  <label className={labelClass} data-id="element-7368">
                    {t('components.InstallmentPayment.total_payment_amount')}{' '}
                    <span className="text-red-400" data-id="element-7369">*</span>
                  </label>
                  <div className="relative" data-id="element-7370">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7371">
                      <span className="text-sm font-medium text-gray-500" data-id="element-7372">
                        {t('components.BulkPaymentListSimplified.usd')}
                      </span>
                    </div>
                    <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={totalPaymentAmount} onChange={e => handleTotalPaymentAmountChange(e.target.value)} data-id="element-7373" />
                  </div>
                </div>
                <div data-id="element-7374">
                  <label className={labelClass} data-id="element-7375">
                    {t('components.InstallmentPayment.commission_type')}
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-7376">
                    <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${beneficiaryType === 'percentage' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-7377">
                      <input type="radio" name="beneficiaryType" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={beneficiaryType === 'percentage'} onChange={() => setBeneficiaryType('percentage')} data-id="element-7378" />

                      <div className="flex items-center gap-2" data-id="element-7379">
                        <PercentIcon size={14} className={beneficiaryType === 'percentage' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-7380" />

                        <span className="text-sm font-medium text-gray-900" data-id="element-7381">
                          {t('components.InstallmentPayment.percentage')}
                        </span>
                      </div>
                    </label>

                    <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${beneficiaryType === 'fixed' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-7382">
                      <input type="radio" name="beneficiaryType" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={beneficiaryType === 'fixed'} onChange={() => setBeneficiaryType('fixed')} data-id="element-7383" />

                      <div className="flex items-center gap-2" data-id="element-7384">
                        <DollarSignIcon size={14} className={beneficiaryType === 'fixed' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-7385" />

                        <span className="text-sm font-medium text-gray-900" data-id="element-7386">
                          {t('components.InstallmentPayment.fixed_amount')}
                        </span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {commissionError && <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-start border border-red-100" data-id="element-7387">
                  <AlertCircleIcon size={18} className="mr-3 mt-0.5 flex-shrink-0" data-id="element-7388" />

                  <span className="text-sm font-medium" data-id="element-7389">{commissionError}</span>
                </div>}

              {suppliers.map((supplier, index) => <div key={supplier.id} className="flex items-start space-x-4 p-5 bg-gray-50 rounded-xl border border-gray-100" data-id="element-7390">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#E2231A]/10 text-[#E2231A] rounded-full font-bold text-sm flex-shrink-0" data-id="element-7391">
                    {index + 1}
                  </div>
                  <div className="flex-1 space-y-4" data-id="element-7392">
                    <div data-id="element-7393">
                      <label className={labelClass} data-id="element-7394">
                        {t('components.InstallmentPayment.select_supplier')}
                      </label>
                      <SearchableSelect options={supplierOptions} value={supplier.name} onChange={val => {
                  const selected = mockSuppliers.find(s => s.name === val);
                  // Update both name and email in a single state call to avoid stale state
                  setSuppliers(suppliers.map(s => s.id === supplier.id ? {
                    ...s,
                    name: selected ? selected.name : val,
                    email: selected ? selected.email : s.email
                  } : s));
                }} placeholder={t('components.InstallmentPayment.attrs.select_a_supplier')} data-id="element-7395" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-7396">
                      <div data-id="element-7397">
                        <label className={labelClass} data-id="element-7398">
                          {beneficiaryType === 'percentage' ? 'Commission percentage' : 'Fixed amount'}
                        </label>
                        <div className="relative" data-id="element-7399">
                          {beneficiaryType === 'percentage' ? <>
                              <input type="number" className={inputClass} placeholder="0" min="0" max="100" value={supplier.percentage} onChange={e => updateSupplier(supplier.id, 'percentage', e.target.value)} data-id="element-7400" />

                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-7401">
                                <span className="text-sm font-medium text-gray-500" data-id="element-7402">
                                  %
                                </span>
                              </div>
                            </> : <>
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7403">
                                <span className="text-sm font-medium text-gray-500" data-id="element-7404">
                                  {t('components.BulkPaymentListSimplified.usd')}
                                </span>
                              </div>
                              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={supplier.amount} onChange={e => updateSupplier(supplier.id, 'amount', e.target.value)} data-id="element-7405" />
                            </>}
                        </div>
                        {beneficiaryType === 'percentage' && parseFloat(supplier.percentage || '0') > 0 && totalPaymentAmount && <div className="text-xs font-medium text-[#9F140F] mt-2" data-id="element-7406">
                              {formatCurrencyAmount(parseFloat(totalPaymentAmount) * parseFloat(supplier.percentage || '0') / 100, {
                      fractionDigits: 2
                    })}
                            </div>}
                      </div>
                      <div data-id="element-7407">
                        <label className={labelClass} data-id="element-7408">
                          {t('components.BulkPaymentPreview.description')}
                        </label>
                        <input type="text" className={inputClass} placeholder={t('components.InstallmentPayment.attrs.eg_commission_for_services')} value={supplier.description || ''} onChange={e => updateSupplier(supplier.id, 'description', e.target.value)} data-id="element-7409" />
                      </div>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeSupplier(supplier.id)} className="text-gray-400 hover:text-red-500 mt-1 transition-colors" disabled={suppliers.length <= 1} data-id="element-7410">
                    <TrashIcon size={18} data-id="element-7411" />
                  </button>
                </div>)}

              <button type="button" onClick={addSupplier} className="mt-4 flex items-center text-sm font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-7412">
                <PlusIcon size={16} className="mr-1.5" data-id="element-7413" />
                {t('components.InstallmentPayment.add_another_supplier')}
              </button>
            </div>}

          {/* Dates and Recipients sections */}
          {(splitType === 'dates' || splitType === 'recipients') && <div className="space-y-4" data-id="element-7414">
              {installments.map((installment, index) => <div key={installment.id} className="flex items-start space-x-4 p-5 bg-gray-50 rounded-xl border border-gray-100" data-id="element-7415">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#E2231A]/10 text-[#E2231A] rounded-full font-bold text-sm flex-shrink-0" data-id="element-7416">
                    {index + 1}
                  </div>
                  <div className="flex-1 space-y-4" data-id="element-7417">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-7418">
                      <div data-id="element-7419">
                        <label className={labelClass} data-id="element-7420">
                          {t('components.BulkPaymentPreview.amount')}
                        </label>
                        <div className="relative" data-id="element-7421">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7422">
                            <span className="text-sm font-medium text-gray-500" data-id="element-7423">
                              {t('components.BulkPaymentListSimplified.usd')}
                            </span>
                          </div>
                          <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={installment.amount} onChange={e => updateInstallment(installment.id, 'amount', e.target.value)} data-id="element-7424" />
                        </div>
                      </div>

                      {splitType === 'dates' ? <>
                          <div className="relative" data-id="element-7425">
                            <label className={labelClass} data-id="element-7426">
                              {t('components.InstallmentPayment.due_date')}
                            </label>
                            <div className="relative" data-id="element-7427">
                              <input type="text" className={`${inputClass} pl-10 cursor-pointer`} placeholder={t('components.CommonPaymentOptions.attrs.mmddyyyy')} value={installment.dueDate} onChange={e => updateInstallment(installment.id, 'dueDate', e.target.value)} onClick={() => toggleDatePicker(installment.id, 'due')} readOnly data-id="element-7428" />

                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7429">
                                <CalendarIcon size={16} className="text-gray-400" data-id="element-7430" />
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => toggleDatePicker(installment.id, 'due')} data-id="element-7431">
                                {showDatePickers[installment.id]?.due ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-7432" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-7433" />}
                              </div>
                            </div>

                            {/* Date Picker for Due Date */}
                            {showDatePickers[installment.id]?.due && <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-full min-w-[280px]" data-id="element-7434">
                                {(() => {
                        const calendar = generateCalendar(installment.dueDate);
                        return <>
                                      <div className="flex justify-between items-center mb-3" data-id="element-7435">
                                        <button type="button" className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => handlePrevMonth(calendar, installment.id, 'due')} data-id="element-7436">
                                          <ChevronDownIcon size={16} className="transform rotate-90 text-gray-600" data-id="element-7437" />
                                        </button>
                                        <div className="font-semibold text-gray-900 text-sm" data-id="element-7438">
                                          {calendar.month.toLocaleString('default', {
                                month: 'long',
                                year: 'numeric'
                              })}
                                        </div>
                                        <button type="button" className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => handleNextMonth(calendar, installment.id, 'due')} data-id="element-7439">
                                          <ChevronDownIcon size={16} className="transform -rotate-90 text-gray-600" data-id="element-7440" />
                                        </button>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium mb-2" data-id="element-7441">
                                        <div className="text-gray-400" data-id="element-7442">
                                          {t('components.FixedLinksPayment.su')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-7443">
                                          {t('components.FixedLinksPayment.mo')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-7444">
                                          {t('components.FixedLinksPayment.tu')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-7445">
                                          {t('components.FixedLinksPayment.we')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-7446">
                                          {t('components.FixedLinksPayment.th')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-7447">
                                          {t('components.FixedLinksPayment.fr')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-7448">
                                          {t('components.FixedLinksPayment.sa')}
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-7449">
                                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors mx-auto
                                                  ${isSelected(date, installment.dueDate) ? 'bg-[#E2231A] text-white shadow-sm' : ''}
                                                  ${isToday(date) && !isSelected(date, installment.dueDate) ? 'border border-[#E2231A] text-[#9F140F]' : ''}
                                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'}
                                                `} onClick={() => handleDateSelect(installment.id, 'dueDate', date)} data-id="element-7450">
                                                {date.getDate()}
                                              </button> : <div key={index} className="w-8 h-8" data-id="element-7451"></div>))}
                                      </div>
                                      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between" data-id="element-7452">
                                        <button type="button" onClick={() => handleDateSelect(installment.id, 'dueDate', new Date())} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-7453">
                                          Today
                                        </button>
                                        <button type="button" onClick={() => {
                              const tmrw = new Date();
                              tmrw.setDate(tmrw.getDate() + 1);
                              handleDateSelect(installment.id, 'dueDate', tmrw);
                            }} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-7454">
                                          Tomorrow
                                        </button>
                                        <button type="button" onClick={() => {
                              const nextWeek = new Date();
                              nextWeek.setDate(nextWeek.getDate() + 7);
                              handleDateSelect(installment.id, 'dueDate', nextWeek);
                            }} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-7455">
                                          Next Week
                                        </button>
                                      </div>
                                    </>;
                      })()}
                              </div>}
                          </div>

                          <div className="relative" data-id="element-7456">
                            <label className={labelClass} data-id="element-7457">
                              {t('components.InstallmentPayment.send_link_on')}
                            </label>
                            <div className="relative" data-id="element-7458">
                              <input type="text" className={`${inputClass} pl-10 cursor-pointer`} placeholder={t('components.CommonPaymentOptions.attrs.mmddyyyy')} value={installment.sendLinkDate} onChange={e => updateInstallment(installment.id, 'sendLinkDate', e.target.value)} onClick={() => toggleDatePicker(installment.id, 'send')} readOnly data-id="element-7459" />

                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7460">
                                <CalendarIcon size={16} className="text-gray-400" data-id="element-7461" />
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => toggleDatePicker(installment.id, 'send')} data-id="element-7462">
                                {showDatePickers[installment.id]?.send ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-7463" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-7464" />}
                              </div>
                            </div>

                            {/* Date Picker for Send Link Date */}
                            {showDatePickers[installment.id]?.send && <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-full min-w-[280px]" data-id="element-7465">
                                {(() => {
                        const calendar = generateCalendar(installment.sendLinkDate);
                        return <>
                                      <div className="flex justify-between items-center mb-3" data-id="element-7466">
                                        <button type="button" className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => handlePrevMonth(calendar, installment.id, 'send')} data-id="element-7467">
                                          <ChevronDownIcon size={16} className="transform rotate-90 text-gray-600" data-id="element-7468" />
                                        </button>
                                        <div className="font-semibold text-gray-900 text-sm" data-id="element-7469">
                                          {calendar.month.toLocaleString('default', {
                                month: 'long',
                                year: 'numeric'
                              })}
                                        </div>
                                        <button type="button" className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => handleNextMonth(calendar, installment.id, 'send')} data-id="element-7470">
                                          <ChevronDownIcon size={16} className="transform -rotate-90 text-gray-600" data-id="element-7471" />
                                        </button>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium mb-2" data-id="element-7472">
                                        <div className="text-gray-400" data-id="element-7473">
                                          {t('components.FixedLinksPayment.su')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-7474">
                                          {t('components.FixedLinksPayment.mo')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-7475">
                                          {t('components.FixedLinksPayment.tu')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-7476">
                                          {t('components.FixedLinksPayment.we')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-7477">
                                          {t('components.FixedLinksPayment.th')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-7478">
                                          {t('components.FixedLinksPayment.fr')}
                                        </div>
                                        <div className="text-gray-400" data-id="element-7479">
                                          {t('components.FixedLinksPayment.sa')}
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-7480">
                                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors mx-auto
                                                  ${isSelected(date, installment.sendLinkDate) ? 'bg-[#E2231A] text-white shadow-sm' : ''}
                                                  ${isToday(date) && !isSelected(date, installment.sendLinkDate) ? 'border border-[#E2231A] text-[#9F140F]' : ''}
                                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'}
                                                `} onClick={() => handleDateSelect(installment.id, 'sendLinkDate', date)} data-id="element-7481">
                                                {date.getDate()}
                                              </button> : <div key={index} className="w-8 h-8" data-id="element-7482"></div>))}
                                      </div>
                                      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between" data-id="element-7483">
                                        <button type="button" onClick={() => handleDateSelect(installment.id, 'sendLinkDate', new Date())} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-7484">
                                          Today
                                        </button>
                                        <button type="button" onClick={() => {
                              const tmrw = new Date();
                              tmrw.setDate(tmrw.getDate() + 1);
                              handleDateSelect(installment.id, 'sendLinkDate', tmrw);
                            }} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-7485">
                                          Tomorrow
                                        </button>
                                        <button type="button" onClick={() => {
                              const nextWeek = new Date();
                              nextWeek.setDate(nextWeek.getDate() + 7);
                              handleDateSelect(installment.id, 'sendLinkDate', nextWeek);
                            }} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-7486">
                                          Next Week
                                        </button>
                                      </div>
                                    </>;
                      })()}
                              </div>}
                          </div>
                        </> : <div className="col-span-2" data-id="element-7487">
                          <label className={labelClass} data-id="element-7488">
                            {t('components.InstallmentPayment.recipient_contact_information')}
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-7489">
                            <div className="relative" data-id="element-7490">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7491">
                                <MailIcon size={16} className="text-gray-400" data-id="element-7492" />
                              </div>
                              <input type="email" className={`${inputClass} pl-10`} placeholder={t('components.InstallmentPayment.attrs.email_address')} value={installment.recipient?.email || ''} onChange={e => updateRecipient(installment.id, 'email', e.target.value)} data-id="element-7493" />
                            </div>
                            <div className="relative" data-id="element-7494">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7495">
                                <SmartphoneIcon size={16} className="text-gray-400" data-id="element-7496" />
                              </div>
                              <input type="tel" className={`${inputClass} pl-10`} placeholder={t('components.InstallmentPayment.attrs.phone_number')} value={installment.recipient?.phone || ''} onChange={e => updateRecipient(installment.id, 'phone', e.target.value)} data-id="element-7497" />
                            </div>
                          </div>
                        </div>}
                    </div>

                    <div data-id="element-7498">
                      <label className={labelClass} data-id="element-7499">
                        {t('components.BulkPaymentPreview.description')}
                      </label>
                      <input type="text" className={inputClass} placeholder={splitType === 'dates' ? 'e.g., First installment' : "e.g., John's share"} value={installment.description} onChange={e => updateInstallment(installment.id, 'description', e.target.value)} data-id="element-7500" />
                    </div>
                  </div>

                  <button type="button" onClick={() => removeInstallment(installment.id)} className="text-gray-400 hover:text-red-500 mt-1 transition-colors" disabled={installments.length <= 1} data-id="element-7501">
                    <TrashIcon size={18} data-id="element-7502" />
                  </button>
                </div>)}

              <button type="button" onClick={addInstallment} className="mt-4 flex items-center text-sm font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-7503">
                <PlusIcon size={16} className="mr-1.5" data-id="element-7504" />
                {t('components.InstallmentPayment.add_another')}{' '}
                {splitType === 'dates' ? 'payment date' : 'recipient'}
              </button>
            </div>}
        </div>
      </div>

      {/* ─── FEE CONFIGURATION ─── */}
      {splitType === 'commission' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7505">
          <div className="px-6 py-4 border-b border-gray-100" data-id="element-7506">
            <h4 className="text-sm font-semibold text-gray-900" data-id="element-7507">
              {t('components.InstallmentPayment.fee_configuration')}
            </h4>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-7508">
              {t('components.InstallmentPayment.choose_who_pays_the_transaction_processing')}
            </p>
          </div>
          <div className="p-6 space-y-5" data-id="element-7509">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-7510">
              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${feeType === 'supplier' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-7511">
                <input type="radio" name="feeType" className="mt-0.5 h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={feeType === 'supplier'} onChange={() => {
              setFeeType('supplier');
              setPassFeesToCustomer(false);
            }} data-id="element-7512" />

                <div data-id="element-7513">
                  <div className="flex items-center gap-2 mb-1" data-id="element-7514">
                    <BuildingIcon size={16} className={feeType === 'supplier' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-7515" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-7516">
                      {t('components.InstallmentPayment.supplier_pays')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-7517">
                    {t('components.InstallmentPayment.fee_is_deducted_from_the_suppliers')}
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${feeType === 'merchant' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-7518">
                <input type="radio" name="feeType" className="mt-0.5 h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={feeType === 'merchant'} onChange={() => {
              setFeeType('merchant');
              setPassFeesToCustomer(false);
            }} data-id="element-7519" />

                <div data-id="element-7520">
                  <div className="flex items-center gap-2 mb-1" data-id="element-7521">
                    <UserIcon size={16} className={feeType === 'merchant' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-7522" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-7523">
                      {t('components.InstallmentPayment.my_business_pays')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-7524">
                    {t('components.InstallmentPayment.fee_is_deducted_from_your_share')}
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${feeType === 'split' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-7525">
                <input type="radio" name="feeType" className="mt-0.5 h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={feeType === 'split'} onChange={() => {
              setFeeType('split');
              setPassFeesToCustomer(false);
            }} data-id="element-7526" />

                <div data-id="element-7527">
                  <div className="flex items-center gap-2 mb-1" data-id="element-7528">
                    <PercentIcon size={16} className={feeType === 'split' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-7529" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-7530">
                      {t('components.InstallmentPayment.split_the_fee')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-7531">
                    {t('components.InstallmentPayment.share_the_fee_between_your_business')}
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${feeType === 'customer' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-7532">
                <input type="radio" name="feeType" className="mt-0.5 h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={feeType === 'customer'} onChange={() => {
              setFeeType('customer');
              setPassFeesToCustomer(true);
            }} data-id="element-7533" />

                <div data-id="element-7534">
                  <div className="flex items-center gap-2 mb-1" data-id="element-7535">
                    <UsersIcon size={16} className={feeType === 'customer' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-7536" />

                    <span className="text-sm font-semibold text-gray-900" data-id="element-7537">
                      {t('components.InstallmentPayment.customer_pays')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-7538">
                    {t('components.InstallmentPayment.fee_is_added_to_the_total')}
                  </p>
                </div>
              </label>
            </div>

            {feeType === 'split' && <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100" data-id="element-7539">
                <label className={labelClass} data-id="element-7540">
                  {t('components.InstallmentPayment.supplier_fee_percentage')}
                </label>
                <div className="relative w-full sm:w-1/3" data-id="element-7541">
                  <input type="number" className={inputClass} placeholder="50" min="0" max="100" defaultValue="50" data-id="element-7542" />

                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-7543">
                    <span className="text-sm font-medium text-gray-500" data-id="element-7544">%</span>
                  </div>
                </div>
              </div>}
          </div>
        </div>}

      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} passFeesToCustomer={passFeesToCustomer} onPassFeesToCustomerChange={val => {
      setPassFeesToCustomer(val);
      if (val) {
        setFeeType('customer');
      } else if (feeType === 'customer') {
        setFeeType('supplier');
      }
    }} data-id="element-7545" />
    </div>;
}